import { createFileRoute } from "@tanstack/react-router";
import {
  IndustryCityMarketingPage,
  buildIndustryCityMarketingHead,
  type IndustryCityMarketingConfig,
} from "@/components/IndustryCityMarketingPage";

const CONFIG: IndustryCityMarketingConfig = {
  slug: "hvac-marketing-colorado-springs",
  industry: "HVAC",
  industryLower: "hvac",
  city: "Colorado Springs",
  citySlug: "colorado-springs",
  cityGeo: { lat: 38.8339, lng: -104.8214 },
  zips: [
    "80903", "80904", "80906", "80907", "80908", "80909",
    "80915", "80916", "80917", "80918", "80919", "80920",
    "80921", "80922", "80923", "80924",
  ],
  neighborhoods: [
    "Briargate", "Powers Corridor", "Northgate", "Black Forest",
    "Old Colorado City", "Falcon", "Monument", "Security-Widefield",
  ],
  nearbyCities: ["Monument", "Fountain", "Falcon", "Manitou Springs"],

  metaTitle: "HVAC Marketing in Colorado Springs | SEO, Maps, Ads & Web",
  metaDescription:
    "Owner-led HVAC marketing for Colorado Springs — SEO, Google Maps, web design, and ads tuned for Briargate, Powers, Falcon, and Monument. Flat $1,650/mo, no contracts.",

  h1: {
    lead: "HVAC marketing in",
    gold: "Colorado Springs",
    trail: "— built to fill your schedule year-round",
  },
  eyebrow: "Colorado Springs HVAC Marketing",
  heroDescription:
    "We help Colorado Springs HVAC companies dominate the Map Pack, win emergency-call SEO, and keep techs booked through every shoulder season — from Briargate furnaces to Powers Corridor AC installs.",

  intro:
    "Springs HVAC is a brutal market in shoulder seasons — and a gold rush during the first 95°F week or the first October cold snap. The companies that win year-round aren't the ones with the loudest radio ads. They're the ones that own the Map Pack when someone in Briargate searches 'AC repair near me' at 9pm, and whose website loads fast enough on a phone in a Powers Corridor parking lot to actually get the call. That's what we build — owner-led from our Woodmen Rd office, flat $1,650/month, no contracts.",

  painPoints: [
    "Slow spring and fall seasons with no inbound calls between the AC-install rush and the furnace rush.",
    "Losing emergency-repair calls to one or two Springs HVAC competitors who own the top 3 Map Pack spots.",
    "A website that looks fine on a desktop but loses mobile callers in under 3 seconds — the only device that matters for emergency HVAC.",
  ],

  services: {
    seo: "Rank for 'AC repair Colorado Springs', 'furnace repair near me', and every neighborhood + service combo across Briargate, Powers, Falcon, and Monument.",
    googleMaps: "Own the top-3 Map Pack for Springs HVAC — categories, services, geo-tagged photos, weekly GBP posts, and a real review-velocity engine.",
    webDesign: "Hand-coded HVAC sites that load sub-2s on a Springs mobile network and convert emergency callers with sticky tap-to-call.",
    ppc: "Profitable Google Ads tuned to Springs seasons — emergency-repair bidding ramps in July and December, ROI-tracked per ZIP.",
    geoAi: "Get cited when Springs homeowners ask ChatGPT, Gemini, or Perplexity 'best HVAC company in Colorado Springs.'",
    socialMedia: "Local trust-building content that turns one-time repair customers into annual maintenance contracts.",
  },

  trustPoints: [
    "Headquartered at 3210 E Woodmen Rd — we work in your service area, not a coastal agency cubicle.",
    "300+ Colorado clients served since 2006, including HVAC, plumbing, and electrical trades.",
    "Flat $1,650/month marketing, websites from $1,850 — published publicly, no discovery-call shell game.",
    "Talk to Chris directly. No account managers, no sales reps, no offshore handoffs.",
  ],

  testimonial: {
    quote:
      "Our phone hasn't stopped ringing since they rebuilt our site and dialed in our Google Maps. Best marketing investment we've ever made — and the first agency that actually picked up the phone when I called.",
    role: "HVAC company owner",
    location: "Colorado Springs",
  },

  faqs: [
    {
      q: "How do I get more HVAC leads in Colorado Springs?",
      a: "The highest-ROI path is local SEO + a tuned Google Business Profile so you show up in the Map Pack for 'AC repair near me' and 'furnace repair Colorado Springs', backed by a website that converts mobile callers in under 3 seconds. Layer Google Ads on top during shoulder seasons (April–May, September–October) to keep techs booked through the slow months.",
    },
    {
      q: "How much does HVAC marketing cost in Colorado Springs?",
      a: "Our Growth Marketing package — SEO, Google Maps, GEO AI, and social — is $1,650/month flat. A new HVAC website starts at $1,850 one-time, owned outright. We also offer in-house financing starting at 4 monthly payments for new sites. Prices are published publicly because Springs owners deserve a straight answer.",
    },
    {
      q: "How long until I see Map Pack movement for my Springs HVAC company?",
      a: "Most Springs HVAC clients see meaningful Map Pack movement within 60–120 days. Full citywide dominance (top-3 across Briargate, Powers, Falcon, and Monument) usually takes 4–6 months of consistent work. It compounds — month 6 is dramatically stronger than month 2.",
    },
    {
      q: "Do you cover Monument, Falcon, and Black Forest too?",
      a: "Yes. We build neighborhood-level landing copy and Map-Pack signals for every Springs corridor — Briargate, Powers, Northgate, Black Forest, Old Colorado City, Falcon, Monument, and Security-Widefield. If your trucks roll into a ZIP, we make sure that ZIP knows your name.",
    },
    {
      q: "Should I run Google Ads or focus on SEO first?",
      a: "If you need calls this week, start Google Ads — we can have a tuned Springs HVAC campaign live in 5 business days. If you want a lead engine that doesn't shut off when you pause spend, start SEO + Google Maps. Most of our successful Springs HVAC clients run both: ads for immediate flow during emergencies, SEO for the durable foundation.",
    },
    {
      q: "Are you actually local to Colorado Springs?",
      a: "Yes. Our office is at 3210 E Woodmen Rd STE 110, Colorado Springs, CO 80920. Verified Google Business Profile, 4.9★ average rating. You can stop by, meet Chris, and see the team. We've been a Springs digital marketing agency since 2006.",
    },
    {
      q: "Do you lock me into a contract?",
      a: "No. Every Colorado Web Impressions engagement is month-to-month. If we're not earning our keep, you cancel — no early-termination fees, no clawbacks. We earn the renewal every month.",
    },
  ],

  related: [
    {
      to: "/hvac-marketing-colorado",
      label: "HVAC marketing across Colorado",
      description: "Statewide HVAC marketing hub — every service we offer for Colorado HVAC companies.",
    },
    {
      to: "/colorado-springs-hvac-seo",
      label: "Colorado Springs HVAC SEO",
      description: "Deep dive on SEO specifically for Springs HVAC companies and the Map Pack.",
    },
    {
      to: "/hvac-google-maps-colorado",
      label: "HVAC Google Maps optimization",
      description: "Map Pack dominance for HVAC companies across every Colorado city we serve.",
    },
    {
      to: "/colorado-springs",
      label: "Colorado Springs digital marketing hub",
      description: "Every service we offer for Colorado Springs businesses in one place.",
    },
    {
      to: "/pricing",
      label: "Flat, public pricing",
      description: "$1,650/mo marketing, websites from $1,850 — plus in-house financing for new sites.",
    },
    {
      to: "/meet-chris",
      label: "Meet Chris",
      description: "The owner you'll actually talk to — not a salesperson or account manager.",
    },
  ],

  showPricing: true,
};

export const Route = createFileRoute("/hvac-marketing-colorado-springs")({
  head: () => buildIndustryCityMarketingHead(CONFIG),
  component: () => <IndustryCityMarketingPage config={CONFIG} />,
});
